import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type Frequency = 'monthly' | 'quarterly';
export type SurchargeMode = 'flat' | 'annual_financed';

export type PricingPlan = {
  id: string;
  projectId: string;
  name: string;
  termMonths: number;
  downPaymentPercent: string; // Decimal serialized as string
  surchargePerSqm: string; // BigInt tiyin as string (flat: per m²; annual: per m²/year)
  surchargeMode: SurchargeMode;
  frequency: Frequency;
  active: boolean;
  position: number;
  createdAt: string;
  updatedAt: string;
};

export type CreatePricingPlanInput = {
  name: string;
  termMonths: number;
  downPaymentPercent: number;
  surchargePerSqm: string; // tiyin string
  surchargeMode: SurchargeMode;
  frequency: Frequency;
  active?: boolean;
  position?: number;
};

export type UpdatePricingPlanInput = Partial<CreatePricingPlanInput>;

export function useProjectPricingPlans(projectId: string | undefined, includeInactive = false) {
  return useQuery({
    queryKey: ['pricing-plans', projectId, includeInactive],
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ data: PricingPlan[] }>(`/projects/${projectId}/pricing-plans`, {
        params: includeInactive ? { includeInactive: 'true' } : undefined,
      });
      return res.data.data;
    },
  });
}

export function useCreatePricingPlan(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePricingPlanInput) => {
      const res = await api.post<{ data: PricingPlan }>(
        `/projects/${projectId}/pricing-plans`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pricing-plans', projectId] });
    },
  });
}

export function useUpdatePricingPlan(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePricingPlanInput }) => {
      const res = await api.patch<{ data: PricingPlan }>(`/pricing-plans/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pricing-plans', projectId] });
    },
  });
}

export function useDeactivatePricingPlan(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ data: PricingPlan }>(`/pricing-plans/${id}/deactivate`);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pricing-plans', projectId] });
    },
  });
}

export function useActivatePricingPlan(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ data: PricingPlan }>(`/pricing-plans/${id}/activate`);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pricing-plans', projectId] });
    },
  });
}
